import React, { useState, useRef } from 'react';
import { useAuth } from '../../../context/AuthContext';
import api from '../../../utils/api';

export default function ProfilePanel({ user, onUpdate }) {
  const { updateUser } = useAuth();

  const [tab, setTab] = useState('info'); // 'info' | 'password' | 'photo'
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });

  const [form, setForm] = useState({
    name:         user?.name         || '',
    phone:        user?.phone        || '',
    address:      user?.address      || '',
    city:         user?.city         || '',
    state:        user?.state        || '',
    pincode:      user?.pincode      || '',
    businessName: user?.businessName || '',
  });

  const [pwForm, setPwForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  const [showPw, setShowPw] = useState({
    current_password: false,
    new_password: false,
    confirm_password: false,
  });

  const fileRef = useRef();
  const [previewImg, setPreviewImg] = useState(user?.profile_image || null);
  const [uploadingImg, setUploadingImg] = useState(false);

  const role = user?.role || 'customer';

  const showMsg = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type: '', text: '' }), 3500);
  };

  // ── Save profile ──────────────────────────────────────────────
  const handleSaveProfile = async () => {
    if (!form.name.trim()) return showMsg('error', 'Full name is required.');
    setSaving(true);
    try {
      const { data } = await api.put(`/api/${role}/profile`, form);
      updateUser?.(data.user);
      onUpdate?.(data.user);
      setEditing(false);
      showMsg('success', 'Profile updated successfully!');
    } catch (err) {
      showMsg('error', err.response?.data?.error || 'Could not update profile.');
    } finally {
      setSaving(false);
    }
  };

  // ── Change password ───────────────────────────────────────────
  const handleChangePassword = async () => {
    if (!pwForm.current_password || !pwForm.new_password)
      return showMsg('error', 'All fields are required.');
    if (pwForm.new_password.length < 6)
      return showMsg('error', 'New password must be at least 6 characters.');
    if (pwForm.new_password !== pwForm.confirm_password)
      return showMsg('error', 'Passwords do not match.');
    setSaving(true);
    try {
      await api.put(`/api/${role}/change-password`, {
        current_password: pwForm.current_password,
        new_password:     pwForm.new_password,
      });
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
      showMsg('success', 'Password changed successfully!');
    } catch (err) {
      showMsg('error', err.response?.data?.error || 'Could not change password.');
    } finally {
      setSaving(false);
    }
  };

  // ── Upload profile image ──────────────────────────────────────
  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024)
      return showMsg('error', 'Image must be under 5MB.');

    setPreviewImg(URL.createObjectURL(file));
    setUploadingImg(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const { data } = await api.put(`/api/${role}/profile-image`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      updateUser?.({ ...user, profile_image: data.profile_image });
      onUpdate?.({ ...user, profile_image: data.profile_image });
      showMsg('success', 'Profile photo updated!');
    } catch (err) {
      showMsg('error', err.response?.data?.error || 'Upload failed.');
      setPreviewImg(user?.profile_image || null);
    } finally {
      setUploadingImg(false);
    }
  };

  // ── Shared input style ────────────────────────────────────────
  const inp = "w-full px-3 py-2 text-sm border border-outline-variant/30 rounded-lg focus:ring-1 focus:ring-primary outline-none bg-surface-container-low disabled:opacity-50";
  const lbl = "block text-[10px] font-bold uppercase tracking-widest text-secondary mb-0.5";

  const tabs = [
    { id: 'info',     icon: 'person',       label: 'Profile'  },
    { id: 'password', icon: 'lock',         label: 'Password' },
    { id: 'photo',    icon: 'photo_camera', label: 'Photo'    },
  ];

  return (
    <div className="max-w-xl mx-auto space-y-4">

      {/* ── Avatar card ── */}
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-outline-variant/10 flex items-center gap-4">
        <div className="relative flex-shrink-0">
          {previewImg ? (
            <img src={previewImg} alt="avatar"
              className="w-16 h-16 rounded-full object-cover border-4 border-emerald-100" />
          ) : (
            <div className="w-16 h-16 rounded-full bg-emerald-600 flex items-center justify-center
                            text-white font-black text-2xl border-4 border-emerald-100">
              {(user?.name || 'U')[0].toUpperCase()}
            </div>
          )}
          {uploadingImg && (
            <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center">
              <span className="material-symbols-outlined text-white animate-spin" style={{fontSize:18}}>progress_activity</span>
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-bold text-lg text-on-surface truncate">{user?.name || 'User'}</h2>
          <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold capitalize">
            {role} Account
          </span>
          <p className="text-xs text-secondary mt-1 truncate">{user?.email}</p>
        </div>
      </div>

      {/* ── Toast ── */}
      {msg.text && (
        <div className={`px-4 py-2.5 rounded-xl text-sm font-medium flex items-center gap-2
          ${msg.type === 'success'
            ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
            : 'bg-red-50 border border-red-200 text-red-700'}`}>
          <span className="material-symbols-outlined text-base">
            {msg.type === 'success' ? 'check_circle' : 'error'}
          </span>
          {msg.text}
        </div>
      )}

      {/* ── Tabs ── */}
      <div className="flex gap-1 bg-surface-container rounded-xl p-1">
        {tabs.map(t => (
          <button key={t.id} onClick={() => { setTab(t.id); setEditing(false); }}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold transition-all
              ${tab === t.id ? 'bg-white shadow text-primary' : 'text-secondary hover:text-on-surface'}`}>
            <span className="material-symbols-outlined" style={{fontSize:15}}>{t.icon}</span>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Tab: Profile Info ── */}
      {tab === 'info' && (
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-outline-variant/10 space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-bold text-base">Account Details</h3>
            {!editing ? (
              <button onClick={() => setEditing(true)}
                className="flex items-center gap-1 text-xs font-bold text-primary hover:underline">
                <span className="material-symbols-outlined" style={{fontSize:15}}>edit</span> Edit
              </button>
            ) : (
              <button onClick={() => { setEditing(false); showMsg('', ''); }}
                className="text-xs font-bold text-secondary hover:text-on-surface">
                Cancel
              </button>
            )}
          </div>

          {editing ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={lbl}>Full Name *</label>
                  <input className={inp} value={form.name}
                    onChange={e => setForm({...form, name: e.target.value})}
                    placeholder="Full name" />
                </div>
                <div>
                  <label className={lbl}>Phone</label>
                  <input className={inp} value={form.phone}
                    onChange={e => setForm({...form, phone: e.target.value})}
                    placeholder="Phone number" />
                </div>
              </div>

              {(role === 'farmer' || role === 'butcher') && (
                <div>
                  <label className={lbl}>{role === 'farmer' ? 'Farm Name' : 'Shop Name'}</label>
                  <input className={inp} value={form.businessName}
                    onChange={e => setForm({...form, businessName: e.target.value})}
                    placeholder="Business name" />
                </div>
              )}

              <div>
                <label className={lbl}>Address</label>
                <input className={inp} value={form.address}
                  onChange={e => setForm({...form, address: e.target.value})}
                  placeholder="Street address" />
              </div>

              <div className="grid grid-cols-3 gap-2">
                {['city','state','pincode'].map(f => (
                  <div key={f}>
                    <label className={lbl}>{f.charAt(0).toUpperCase()+f.slice(1)}</label>
                    <input className={inp} value={form[f]}
                      onChange={e => setForm({...form, [f]: e.target.value})}
                      placeholder={f.charAt(0).toUpperCase()+f.slice(1)} />
                  </div>
                ))}
              </div>

              <button onClick={handleSaveProfile} disabled={saving}
                className="w-full py-2 rounded-full bg-primary text-white text-sm font-bold
                           hover:opacity-90 disabled:opacity-50 transition-all">
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>

          ) : (
            // Read-only view
            <div className="space-y-0 divide-y divide-surface-container">
              {[
                { label: 'Full Name',    value: user?.name },
                { label: 'Email',        value: user?.email },
                { label: 'Phone',        value: user?.phone },
                ...(role === 'farmer' || role === 'butcher'
                  ? [{ label: role === 'farmer' ? 'Farm Name' : 'Shop Name', value: user?.businessName }]
                  : []),
                { label: 'City',         value: user?.city },
                { label: 'State',        value: user?.state },
                { label: 'Pincode',      value: user?.pincode },
                { label: 'Role',         value: user?.role },
                { label: 'Member Since', value: user?.created_at
                    ? new Date(user.created_at).toLocaleDateString('en-IN') : '—' },
              ].map(f => (
                <div key={f.label} className="flex justify-between items-center py-2.5">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-secondary">{f.label}</span>
                  <span className="text-sm font-semibold text-on-surface capitalize">{f.value || '—'}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Tab: Password ── */}
      {tab === 'password' && (
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-outline-variant/10 space-y-3">
          <h3 className="font-bold text-base">Change Password</h3>
          {[
            { key: 'current_password', label: 'Current Password', placeholder: 'Enter current password' },
            { key: 'new_password',     label: 'New Password',     placeholder: 'Min. 6 characters' },
            { key: 'confirm_password', label: 'Confirm Password', placeholder: 'Re-enter new password' },
          ].map(f => (
            <div key={f.key}>
              <label className={lbl}>{f.label}</label>
              <div className="relative">
                <input
                  type={showPw[f.key] ? 'text' : 'password'}
                  className={inp + ' pr-10'}
                  value={pwForm[f.key]}
                  placeholder={f.placeholder}
                  onChange={e => setPwForm({...pwForm, [f.key]: e.target.value})}
                />
                <button
                  type="button"
                  onClick={() => setShowPw(prev => ({...prev, [f.key]: !prev[f.key]}))}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary hover:text-on-surface"
                >
                  <span className="material-symbols-outlined" style={{fontSize: 18}}>
                    {showPw[f.key] ? 'visibility_off' : 'visibility'}
                  </span>
                </button>
              </div>
            </div>
          ))}
          <button onClick={handleChangePassword} disabled={saving}
            className="w-full py-2 rounded-full bg-primary text-white text-sm font-bold
                       hover:opacity-90 disabled:opacity-50 transition-all mt-1">
            {saving ? 'Updating…' : 'Update Password'}
          </button>
        </div>
      )}

      {/* ── Tab: Photo ── */}
      {tab === 'photo' && (
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-outline-variant/10 space-y-4">
          <h3 className="font-bold text-base">Profile Photo</h3>

          <div className="flex flex-col items-center gap-4">
            {previewImg ? (
              <img src={previewImg} alt="preview"
                className="w-24 h-24 rounded-full object-cover border-4 border-emerald-100" />
            ) : (
              <div className="w-24 h-24 rounded-full bg-emerald-600 flex items-center justify-center
                              text-white font-black text-3xl border-4 border-emerald-100">
                {(user?.name || 'U')[0].toUpperCase()}
              </div>
            )}

            <input ref={fileRef} type="file" accept="image/*"
              className="hidden" onChange={handleImageChange} />

            <button onClick={() => fileRef.current.click()} disabled={uploadingImg}
              className="flex items-center gap-2 px-5 py-2 rounded-full border-2 border-primary
                         text-primary text-sm font-bold hover:bg-primary/5 disabled:opacity-50 transition-all">
              <span className="material-symbols-outlined" style={{fontSize:16}}>upload</span>
              {uploadingImg ? 'Uploading…' : 'Choose Photo'}
            </button>
            <p className="text-[11px] text-secondary text-center">
              JPG, PNG or WebP • Max 5MB
            </p>
          </div>
        </div>
      )}

    </div>
  );
}